import {EventEmitter, Injectable} from '@angular/core';
import {TaxInformationValidationService} from './validation.service';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {DcpKymaService} from '../../../services/dcp-kyma.service';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DcpCustomerRegistrationDecoratorService} from '../../../services/dcp-customer-registration-decorator.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class UploadAttachmentsService {
  /***
   * main form data holder
   */
  public form: UntypedFormGroup;

  /**
   * defines whether validation error for uploaded files exist
   */
  public isValidationErrorPresent: boolean;
  /**
   * Defines whether the uploaded tax exemption certificate contains the viruses
   */
  public isFileInfected: boolean = false;
  public savedFileCode: string;

  public langUpdated = new EventEmitter();

  public uploadFileToCartSubscription: Subscription = Subscription.EMPTY;
  public removeFileFromCartSubscription: Subscription = Subscription.EMPTY;
  public hasTaxAttachmentsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isTaxExemptionCheckboxCheckedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public showWarningMessageSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public continueToCheckoutSubject: BehaviorSubject<{
    status: boolean;
    userId: string;
    customerId: string;
  }> = new BehaviorSubject<{ status: boolean; userId: string; customerId: string }>({
    status: false,
    userId: undefined,
    customerId: undefined
  });
  public b2bCustomerUpdateCallCompleted: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);


  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly validationService: TaxInformationValidationService,
    public kymaService: DcpKymaService,
    private registrationService: DcpCustomerRegistrationDecoratorService
  ) {
  }

  public async initForm(validationRules): Promise<{ warnings: string[] }> {
    const warnings: string[] = [];

    this.form = this.formBuilder.group({
      attachments: this.formBuilder.array([], [
        this.validationService.createFileTotalSizeValidator(validationRules.UPLOAD_FILES_TOTAL_SIZE_BYTES),
        this.validationService.createFileAmountValidator(validationRules.UPLOAD_FILES_AMOUNT_LIMIT)
      ]),
    });
    return {
      warnings
    };
  }

  /**
   * Attachments form array shortcut
   */
  public get Attachments(): UntypedFormArray {
    if (!this.form) {
      return null;
    }
    return this.form.controls.attachments as UntypedFormArray;
  }

  /**
   * Used to create a form control for added file from <input type="file"...
   *
   * @param file File from input
   * @param validationRules ValidationRules
   * @param shouldUpload defines whether the uploading to commerce should happen
   * @returns void
   */
  public addAttachment(file: File, validationRules, shouldUpload: boolean) {
    if (!this.Attachments) {
      return;
    }
    if (validationRules.UPLOAD_FILES_AMOUNT_LIMIT === 1 && this.Attachments.length > 0) {
      this.removeUploadedFile(0);
    }
    this.Attachments.push(this.formBuilder.control(file, [
      this.validationService.createFileSizeValidator(validationRules.UPLOAD_FILE_SIZE_BYTES),
      this.validationService.createFileTypeValidator(validationRules.UPLOAD_FILE_ALLOWED_TYPES)
    ]));
    if (this.Attachments.errors) {
      this.isValidationErrorPresent = true;
      this.hasTaxAttachmentsSubject.next(false);
      this.showWarningMessageSubject.next(false);
    }
    if (this.Attachments.controls.some(control => control.errors)) {
      this.isValidationErrorPresent = true;
      this.hasTaxAttachmentsSubject.next(false);
      this.showWarningMessageSubject.next(false);
    }
    if (!this.isValidationErrorPresent && shouldUpload) {
      this.uploadFileToCartSubscription = this.kymaService.saveTaxCertificateToCurrentCart(file).pipe(untilDestroyed(this)).subscribe((fileSavedResponse) => {
        this.savedFileCode = fileSavedResponse.code;
        this.hasTaxAttachmentsSubject.next(true);
        this.showWarningMessageSubject.next(false);
        this.isTaxExemptionCheckboxCheckedSubject.next(true);
      }, () => {
        this.isFileInfected = true;
        this.isValidationErrorPresent = true;
        this.langUpdated.emit(true);
      });
    }
    if (!shouldUpload) {
      this.hasTaxAttachmentsSubject.next(true);
    }
  }

  /**
   * Used to remove a file from this.Attachments array by index.
   * Also updates corresponding variables so possible validation error messages are cleared.
   * In this case, there may be the only file in the Attachments array (tax exemption certificate).
   *
   * @param fileIndex
   */
  public removeUploadedFile(fileIndex) {
    this.Attachments.removeAt(fileIndex);
    this.uploadFileToCartSubscription.unsubscribe();
    if (!this.isFileInfected && !this.isValidationErrorPresent) {
      this.removeFileFromCartSubscription = this.kymaService.removeTaxCertificateFromCurrentCart(this.savedFileCode).pipe(untilDestroyed(this)).subscribe(() => {
        this.hasTaxAttachmentsSubject.next(false);
      }, () => {
      });
    }
    this.isFileInfected = false;
    this.isValidationErrorPresent = false;
  }

  public hasTaxAttachments(): Observable<boolean> {
    return this.hasTaxAttachmentsSubject.asObservable();
  }

  public getTaxExemptionCheckboxStatusSubject(): Observable<boolean> {
    return this.isTaxExemptionCheckboxCheckedSubject.asObservable();
  }

  public getWarningMessageSubject(): Observable<boolean> {
    return this.showWarningMessageSubject.asObservable();
  }

  public getContinueToCheckoutSubject(): Observable<{ status: boolean; userId: string; customerId: string }> {
    return this.continueToCheckoutSubject.asObservable();
  }

  public getB2bCustomerUpdateCallCompletedSubject(): Observable<boolean> {
    return this.b2bCustomerUpdateCallCompleted.asObservable();
  }

  public updateB2bCustomerTax(request) {
    this.registrationService.updateTaxConfig(request).pipe(untilDestroyed(this)).subscribe(() => {
        this.continueToCheckoutSubject.next({
          status: false,
          userId: undefined,
          customerId: undefined
        });
      },
      () => {
      },
      () => {
        this.b2bCustomerUpdateCallCompleted.next(true);
      }
    );
  }
}
